import { actionDecision, allows } from "./viewerActions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { itemIdOf } from "../../../api/content/processDefinitions";
import { RunsWithOpenerContext, useRunsWithOpenerSlot } from "../llmChoice/runsWithOpener";
import { useTranslation } from "react-i18next";
import { stepProgressOf, type ThreadOverview } from "../../../api/threads/threadDecoders";
import { useOpenedProject } from "../../../projects/useOpenedProject";
import Composer from "../composer/Composer";
import KeepAsMemory from "../../memories/keep/KeepAsMemory";
import ThreadMemories from "../../memories/ThreadMemories";
import { agentMemorySave } from "../../memories/agentSave";
import { memoryUnderReview } from "../../memories/memoryUnderReview";
import { useMemberNames } from "../data/useMemberNames";
import { nameOfStep, NO_STEP_NAMES, stepNamesOf, withEarlierNames } from "../data/stepNames";
import { useThreadOverview } from "../data/useThreadOverview";
import { useEarlierProcesses, useThreadProcess } from "../data/useThreadProcess";
import EntryLog from "../entries/EntryLog";
import { awaitedToolApproval } from "../entries/toolApproval";
import { useThreadLiveClient, useLiveThread } from "../live/threadLiveContext";
import { useThreadPreparation } from "../prepare";
import { appPaths } from "../../../shared/routing/appPaths";
import { parseCollectionVersionRef } from "../../../shared/references/contentReferences";
import type { LiveThreadSnapshot } from "../live/threadLiveClient";
import { lastNumberOf } from "../live/threadLog";
import { NO_PROGRESS } from "../live/turnProgress";
import ThreadActions from "./ThreadActions";
import ThreadHeader from "./ThreadHeader";
import { ThreadContext, type OpenTurnView, type RunApprovals, type ThreadContextValue } from "./threadPageContext";
import { useStepRunner } from "./useStepRunner";
import { RUN_MARGINS_COLUMNS, RUN_MARGINS_GAP, RunMarginsContext, RunMarginsRequestContext, useRunMarginsRoom } from "./runMargins";
import WorkflowSteps from "./steps/WorkflowSteps";
import WorkflowAttach from "./switch/WorkflowAttach";
import { useThreadWorkflows } from "./switch/useWorkflowSwitch";
import { useStepFocus } from "./currentStep/useStepFocus";
import { useStepPlans } from "./currentStep/useStepPlans";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { useAcknowledgeWoke } from "../data/useThreadPlacements";

/** Entries read when a Thread opens; earlier ones on request. */
const INITIAL_ENTRIES = 100;

export interface ThreadViewProps {
  threadId: string;
  highlightedEntry: number | null;
  /** A host offer to answer, from the offer link. */
  hostOfferId: string | null;
}

/** One Thread: header, the live log, the Workflow's steps with the current one open (F33, F100), and the composer. */
export default function ThreadView(props: ThreadViewProps) {
  const { t } = useTranslation();
  const [startAfter, setStartAfter] = useState<number | null>(null);
  const live = useLiveThread(props.threadId, startAfter);
  const { state: overview, reload } = useThreadOverview(props.threadId, live);

  // Start following once the last number is known: recent entries, or from the cited one.
  if (startAfter === null && overview.status === "loaded") {
    const recent = Math.max(0, overview.value.lastEntryNumber - INITIAL_ENTRIES);
    setStartAfter(props.highlightedEntry === null ? recent : Math.min(recent, Math.max(0, props.highlightedEntry - 10)));
  }

  if (overview.status !== "loaded") return <LoadStatus state={overview} loadingLabel={t("threads.page.loading")} loadingClassName="py-10" onRetry={reload} failedClassName="p-4" />;
  return <LoadedThreadView {...props} overview={overview.value} live={live} />;
}

function LoadedThreadView({ threadId, highlightedEntry, hostOfferId, overview, live }: ThreadViewProps & { overview: ThreadOverview; live: LiveThreadSnapshot | undefined }) {
  // The project menu names the Thread's project, not "Choose a project…".
  useOpenedProject(overview.projectId);
  const client = useThreadLiveClient();
  const people = useMemberNames(overview.projectId);
  useAcknowledgeWoke(overview.projectId, threadId);
  const preparation = useThreadPreparation();
  // The run reads its project items at the Thread's bound content version (F103).
  const commit = parseCollectionVersionRef(overview.bindings.content_version)?.commit ?? null;
  const process = useThreadProcess(overview.workflow, overview.projectId, commit);
  // The content version's Workflows: what a switch offers and how the log names them (F126).
  const { state: workflowListing } = useThreadWorkflows(overview.projectId, commit);
  const listing = workflowListing.status === "loaded" ? workflowListing.value : null;
  const workflowNameOf = useCallback(
    (reference: string) => {
      const item = itemIdOf(reference);
      return listing?.workflows.find((workflow) => itemIdOf(workflow.reference) === item)?.name ?? null;
    },
    [listing],
  );
  const loadedProcess = process.state.status === "loaded" ? process.state.value : null;
  const earlierWorkflows = useMemo(() => [...new Set(overview.departures.map((departure) => departure.workflow))], [overview.departures]);
  const earlierProcesses = useEarlierProcesses(earlierWorkflows, overview.projectId, commit);
  const stepNames = useMemo(
    () => withEarlierNames(loadedProcess === null ? NO_STEP_NAMES : stepNamesOf(loadedProcess), [...earlierProcesses.values()].map(stepNamesOf)),
    [loadedProcess, earlierProcesses],
  );
  const stepNameIn = useCallback(
    (workflow: string, stepKey: string) => {
      const found = workflow === overview.workflow ? loadedProcess : (earlierProcesses.get(workflow) ?? null);
      return found === null ? null : (stepNamesOf(found).byKey.get(stepKey) ?? null);
    },
    [overview.workflow, loadedProcess, earlierProcesses],
  );
  const entries = useMemo(() => live?.log.entries ?? [], [live?.log.entries]);
  // This Thread's log: Keep as memory reads only selections made in it (BA16-01).
  const log = useRef<HTMLDivElement>(null);
  // Module C's status of the Thread now (W-22): the overview holds the newer facts of its read and the live state.
  const { projection, detail } = overview;
  const waits = projection.waits ?? [];
  const openHumanStepEntry = projection.open_human_step?.request_entry ?? null;
  // The current step's section, which entries bring into view: this Thread's own (ADR-0039).
  const currentStep = useRef<HTMLElement>(null);
  const lastEntryNumber = Math.max(overview.lastEntryNumber, live === undefined ? 0 : lastNumberOf(live.log));
  const viewerIsHost = people.currentUserId === overview.host;
  const runner = useStepRunner(threadId, lastEntryNumber);
  const mayFork = allows(overview.viewer, "fork");
  // A branch is prepared beside this Thread, so its history stays in view (SB-22, WA-11).
  const forkAt = (atEntry: number) => {
    preparation.prepare({ kind: "fork", projectId: overview.projectId, returnTo: appPaths.thread(threadId), threadId, atEntry });
  };
  const state = overview.state;
  const progress = live?.progress ?? NO_PROGRESS;
  const shownTurn = detail.open_turn ?? null;
  const shownProgress = shownTurn === null ? undefined : progress.get(shownTurn.turn_id);
  const slotWait = shownTurn === null ? undefined : waits.find((wait) => wait.kind === "slot" && wait.turn_id === shownTurn.turn_id);
  const openTurn: OpenTurnView | null =
    shownTurn === null
      ? null
      : {
          turnId: shownTurn.turn_id,
          state: shownTurn.state,
          executor: shownTurn.executor,
          stepKey: shownTurn.step_key,
          requestedAt: shownTurn.requested_at,
          work: shownProgress?.work ?? null,
          modelBytes: shownProgress?.modelBytes ?? null,
          displayState: shownTurn.display_state,
          waitingForSlotSince: slotWait?.since ?? null,
        };
  // Every turn waiting for its tool's approval, with the request the host may answer here (TA-6).
  const runApprovals: RunApprovals = new Map(
    waits.flatMap((wait) => {
      const approval = wait.kind === "approval" ? awaitedToolApproval(wait, threadId, window.location.origin) : null;
      return approval === null ? [] : [[wait.turn_id, approval] as const];
    }),
  );
  const openRuns = useMemo(
    () => new Map([...(detail.open_runs ?? []).map((run) => [run.turn_id, run.display_state] as const), ...(shownTurn === null ? [] : [[shownTurn.turn_id, shownTurn.display_state] as const])]),
    [detail.open_runs, shownTurn],
  );
  const members = useMemo(() => people.members.map((member) => ({ userId: member.userId, displayName: member.displayName })), [people.members]);

  // A cited entry before the entries held: read earlier pages until it is there.
  const base = live?.log.base ?? 0;
  const loadingEarlier = live?.loadingEarlier ?? false;
  useEffect(() => {
    if (highlightedEntry !== null && live !== undefined && highlightedEntry <= base && !loadingEarlier) {
      void client.loadEarlier(threadId);
    }
  }, [client, threadId, highlightedEntry, base, loadingEarlier, live]);

  const superseded = useMemo(() => new Set(detail.superseded_requests ?? []), [detail.superseded_requests]);
  // A memory an agent saves here shows in the Thread's memories at once (ADR-0031 D5).
  const memorySaves = useMemo(() => entries.filter((entry) => entry.payload.kind === "tool_call" && agentMemorySave(entry.payload) !== null).length, [entries]);
  const followsWorkflow = overview.workflow !== null;
  const plans = useStepPlans(threadId, lastEntryNumber, followsWorkflow);
  const workflowProgress = useMemo(() => stepProgressOf(detail), [detail]);
  const order = useMemo(() => Object.keys(workflowProgress), [workflowProgress]);
  // Module C names the step the Run is at, and none once its Workflow was left or completed (W-22).
  const focus = useStepFocus({ threadId, process: loadedProcess, order, current: projection.current_step ?? null, entries, openHumanStepEntry, plans });
  const requestInStep = followsWorkflow && focus.current?.state === "waitingForYou" ? focus.waitingRequest : null;
  const outputsInStep = useMemo(() => new Set(requestInStep?.payload.kind === "human_step_requested" ? requestInStep.payload.inputs.flatMap((input) => input.from_entry ?? []) : []), [requestInStep]);
  const context: ThreadContextValue = {
    threadId,
    overview,
    nameOf: people.nameOf,
    currentUserId: people.currentUserId,
    viewerIsHost,
    highlightedEntry,
    addressedHostOffer: hostOfferId,
    onForkAt: mayFork ? forkAt : null,
    openHumanStepEntry,
    requestInStep: requestInStep?.number ?? null,
    outputsInStep,
    supersededRequests: superseded,
    openTurn,
    openRuns,
    turnProgress: progress,
    runApprovals,
    stepName: (stepKey, step = null) => nameOfStep(stepNames, stepKey, step),
    workflowName: loadedProcess?.workflow.name ?? null,
    workflowNameOf,
    stepNameIn,
    currentStep,
  };

  // The header's Runs-with row opens from the composer and problem entries of this view only (S12).
  const runsWithOpener = useRunsWithOpenerSlot();
  // Margins beside the steps, while a step asks for them: the steps then span the pane, as margin, column, margin,
  // the column narrowed so both fit (Johannes, 4 Nov 11:11); in a pane too narrow for that, the column as it is.
  const [leftMargin, setLeftMargin] = useState<HTMLDivElement | null>(null);
  const [rightMargin, setRightMargin] = useState<HTMLDivElement | null>(null);
  const [pane, setPane] = useState<HTMLDivElement | null>(null);
  const [marginRequests, setMarginRequests] = useState(0);
  const requestMargins = useCallback(() => {
    setMarginRequests((count) => count + 1);
    return () => { setMarginRequests((count) => count - 1); };
  }, []);
  const marginRoom = useRunMarginsRoom(pane);
  const withMargins = marginRequests > 0 && marginRoom;
  const margins = useMemo(() => (withMargins && leftMargin !== null && rightMargin !== null ? { left: leftMargin, right: rightMargin } : null), [withMargins, leftMargin, rightMargin]);
  // The pane's width, centred on the column: what the steps span with margins.
  const paneWide = { width: "100cqw", marginLeft: "calc(50% - 50cqw)" };
  return (
    <ThreadContext.Provider value={context}>
      <RunsWithOpenerContext.Provider value={runsWithOpener}>
      <div className="flex min-h-full flex-col gap-4 pb-4">
        <ThreadHeader
          status={projection.display_status}
          lastEntryNumber={lastEntryNumber}
          actions={<ThreadActions lastEntryNumber={lastEntryNumber} members={members} runner={runner} />}
        />
        <div ref={log} className="flex-1">
          <EntryLog threadId={threadId} live={live} highlightedEntry={highlightedEntry} followEnd={requestInStep === null} />
        </div>
        {/* The steps with the Run page's margins beside them, where the step in view puts its side panes (Johannes, 4 Nov). */}
        <RunMarginsRequestContext.Provider value={requestMargins}>
        <RunMarginsContext.Provider value={margins}>
          <div ref={setPane} aria-hidden="true" className="h-0" style={paneWide} />
          {/* One tree with or without margins, so the steps stay mounted when they come and go (Sol R5). */}
          <div
            className={withMargins ? "grid justify-center" : undefined}
            style={withMargins ? { ...paneWide, gridTemplateColumns: RUN_MARGINS_COLUMNS, columnGap: RUN_MARGINS_GAP } : undefined}
          >
            <div ref={setLeftMargin} className={withMargins ? "flex min-w-0 justify-end" : "hidden"} />
            <div className="min-w-0">
              {followsWorkflow ? (
                <WorkflowSteps focus={focus} progress={workflowProgress} runTo={detail.run_to ?? null} runner={runner} listing={listing} lastEntryNumber={lastEntryNumber} />
              ) : (
                <WorkflowAttach listing={listing} lastEntryNumber={lastEntryNumber} />
              )}
            </div>
            <div ref={setRightMargin} className={withMargins ? "flex min-w-0 justify-start" : "hidden"} />
          </div>
        </RunMarginsContext.Provider>
        </RunMarginsRequestContext.Provider>
        <ThreadMemories projectId={overview.projectId} threadId={threadId} revision={memorySaves} reviewedHere={memoryUnderReview(entries, openHumanStepEntry, state === "closed")} />
        <KeepAsMemory projectId={overview.projectId} threadId={threadId} entries={entries} log={log} />
        {/* Opaque, so the steps scrolled under the message box don't show around it (TR-B1-04). */}
        <div className="sticky bottom-0 z-10 bg-[var(--app-bg)] pt-2 pb-2">
          <Composer
            facts={{
              threadState: state,
              posting: actionDecision(overview.viewer, "post_message"),
              replying: actionDecision(overview.viewer, "request_reply"),
              connectorAttached: overview.attachedConnector !== null,
              connectorOnline: overview.connectorOnline,
              routeChosen: overview.settings.route !== null,
              routeUnavailableBecause: overview.routeUnavailableBecause,
              turnOpen: openTurn !== null,
              openTurnAwaitsApproval: openTurn?.displayState === "approval",
              openTurnWaitsForSlot: openTurn?.displayState === "slot",
            }}
            onFork={() => { forkAt(lastEntryNumber); }}
          />
        </div>
      </div>
      </RunsWithOpenerContext.Provider>
    </ThreadContext.Provider>
  );
}
